import React, { useEffect, useMemo, useRef, useState } from "react";
import { CornerDownLeft, Search } from "lucide-react";

/**
 * Ctrl/⌘ K command palette. `actions` = [{ id, group, label, hint, icon, keywords, run }].
 * Arrow keys move, Enter runs, Esc closes.
 */
const CommandPalette = ({ open, onClose, actions }) => {
  const [q, setQ] = useState("");
  const [idx, setIdx] = useState(0);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  useEffect(() => {
    if (open) {
      setQ("");
      setIdx(0);
      const t = setTimeout(() => inputRef.current && inputRef.current.focus(), 0);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [open]);

  const filtered = useMemo(() => {
    const list = Array.isArray(actions) ? actions : [];
    const s = q.trim().toLowerCase();
    if (!s) return list;
    return list.filter((a) =>
      `${a.label} ${a.keywords || ""} ${a.group || ""}`.toLowerCase().includes(s),
    );
  }, [q, actions]);

  useEffect(() => {
    setIdx(0);
  }, [q]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
      } else if (e.key === "ArrowDown") {
        e.preventDefault();
        setIdx((i) => Math.min(filtered.length - 1, i + 1));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setIdx((i) => Math.max(0, i - 1));
      } else if (e.key === "Enter") {
        e.preventDefault();
        const a = filtered[idx];
        if (a) {
          onClose();
          a.run();
        }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, filtered, idx, onClose]);

  useEffect(() => {
    const el = listRef.current && listRef.current.querySelector('[data-active="true"]');
    if (el && el.scrollIntoView) el.scrollIntoView({ block: "nearest" });
  }, [idx]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-start justify-center pt-[12vh] p-4 bg-black/60 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="glass rounded-3xl card-shadow w-full max-w-xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Command palette"
      >
        <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-600/50">
          <Search className="w-5 h-5 text-cyan-400 flex-shrink-0" />
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Type a command or a panel name…"
            className="flex-1 bg-transparent outline-none text-cyan-100 placeholder-gray-500 text-base"
            aria-label="Search commands"
          />
          <kbd className="text-[10px] text-gray-400 border border-gray-600 rounded px-1.5 py-0.5">
            Esc
          </kbd>
        </div>
        <ul ref={listRef} className="max-h-[50vh] overflow-y-auto py-2" role="listbox">
          {filtered.length === 0 && (
            <li className="px-4 py-3 text-sm text-gray-400">No matches.</li>
          )}
          {filtered.map((a, i) => {
            const showGroup = i === 0 || filtered[i - 1].group !== a.group;
            const active = i === idx;
            const Icon = a.icon;
            return (
              <React.Fragment key={a.id}>
                {showGroup && a.group && (
                  <li className="px-4 pt-2 pb-1 text-[10px] uppercase tracking-wider text-gray-500">
                    {a.group}
                  </li>
                )}
                <li role="option" aria-selected={active} data-active={active ? "true" : "false"}>
                  <button
                    type="button"
                    onMouseEnter={() => setIdx(i)}
                    onClick={() => {
                      onClose();
                      a.run();
                    }}
                    className={`w-full flex items-center gap-3 px-4 py-2.5 text-left text-sm ${
                      active ? "bg-cyan-500/20 text-cyan-100" : "text-gray-300 hover:bg-gray-700/50"
                    }`}
                  >
                    {Icon && <Icon className="w-4 h-4 text-cyan-400 flex-shrink-0" />}
                    <span className="flex-1 truncate">{a.label}</span>
                    {a.hint && (
                      <kbd className="text-[10px] text-gray-500 border border-gray-600/60 rounded px-1 flex-shrink-0">
                        {a.hint}
                      </kbd>
                    )}
                    {active && <CornerDownLeft className="w-3.5 h-3.5 text-gray-500" />}
                  </button>
                </li>
              </React.Fragment>
            );
          })}
        </ul>
        <div className="px-4 py-2 border-t border-gray-600/50 text-[11px] text-gray-500 flex flex-wrap gap-x-4 gap-y-1">
          <span>↑↓ navigate</span>
          <span>↵ run</span>
          <span>N new goal</span>
          <span>T toggle today</span>
          <span>/ search goals</span>
          <span>F focus mode</span>
          <span>W weekly review</span>
        </div>
      </div>
    </div>
  );
};

export default CommandPalette;
